/**
 * Locations master-detail: the hierarchy tree on the left, the selected unit's
 * detail on the right.
 *
 * One fetch (`useLocations`, the full visible set) feeds both the tree (folded
 * in tree.ts) and the move-target picker. Selection and the expanded-branch set
 * live here so a background refetch never collapses the admin's open branches or
 * loses their place.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingRows, Panel } from './OrgUi';
import { LocationTree } from './LocationTree';
import { LocationDetail } from './LocationDetail';
import { LocationFormDialog } from './OrgDialogs';
import { buildTree, rollUpPatientCounts } from './tree';
import { useLocations } from './useOrg';
import type { Location } from '@/api/endpoints/org';

interface CreateTarget {
  parentId?: string;
  parentName?: string;
}

export function LocationsView() {
  const { t } = useTranslation();
  const { data: locations, isLoading, isError, error, refetch } = useLocations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState<CreateTarget | null>(null);

  const tree = useMemo(() => buildTree(locations ?? []), [locations]);
  // Rolled-up subtree census per unit, composed from each node's direct count
  // over the visible tree (ACL-safe: only visible units are in the tree).
  const patientCounts = useMemo(() => rollUpPatientCounts(tree), [tree]);
  const byId = useMemo(() => {
    const m = new Map<string, Location>();
    for (const l of locations ?? []) m.set(l.id, l);
    return m;
  }, [locations]);

  // First load with a small tree: open the roots so the structure is visible at
  // a glance. Only seeds once (when nothing is expanded yet and data arrives).
  useEffect(() => {
    if (!locations || expanded.size > 0) return;
    if (tree.length > 0 && tree.length <= 8) {
      setExpanded(new Set(tree.map((n) => n.location.id)));
    }
    // Seeds ONCE, on the first load. `expanded.size` is the guard, not an input:
    // as a dependency it would re-fire the moment an admin collapses the last
    // open branch and silently re-open every root under them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // Drop a stale selection (e.g. the selected unit was just deleted).
  useEffect(() => {
    if (selectedId && locations && !byId.has(selectedId)) setSelectedId(null);
  }, [locations, byId, selectedId]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selected = selectedId ? byId.get(selectedId) ?? null : null;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
      {/* Tree */}
      <Panel className="flex max-h-[70vh] min-h-[420px] flex-col p-3">
        <div className="mb-2 flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-neutral-900">{t('org.locationsView.units', 'Units')}</h2>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" onClick={() => setCreating({})}>
            {t('org.locationsView.new', '+ New')}
          </Button>
        </div>
        <div className="-mx-1 flex-1 overflow-y-auto px-1">
          {isError && !locations ? (
            <ErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <LoadingRows rows={6} />
          ) : tree.length === 0 ? (
            <EmptyState
              title={t('org.locationsView.emptyTitle', 'No units yet')}
              hint={t(
                'org.locationsView.emptyHint',
                'Create your hospital to start the tree, then add buildings, wards, and beds inside it.',
              )}
              action={
                <Button size="sm" variant="outline" onClick={() => setCreating({})}>
                  {t('org.locationsView.createFirst', 'Create the first unit')}
                </Button>
              }
            />
          ) : (
            <LocationTree
              nodes={tree}
              selectedId={selectedId}
              expanded={expanded}
              patientCounts={patientCounts}
              onSelect={setSelectedId}
              onToggle={toggle}
              onAddChild={(parentId) => {
                setExpanded((prev) => new Set(prev).add(parentId));
                setCreating({ parentId, parentName: byId.get(parentId)?.name });
              }}
            />
          )}
        </div>
      </Panel>

      {/* Detail */}
      <Panel className="min-h-[420px] p-6">
        {selected ? (
          <LocationDetail
            key={selected.id}
            location={selected}
            allLocations={locations ?? []}
            patientCounts={patientCounts}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              title={
                tree.length === 0
                  ? t('org.locationsView.buildOrg', 'Build your organisation')
                  : t('org.locationsView.selectUnit', 'Select a unit')
              }
              hint={
                tree.length === 0
                  ? t('org.locationsView.createFirstHint', 'Create the first unit on the left.')
                  : t(
                      'org.locationsView.selectUnitHint',
                      'Pick a unit on the left to manage who works there and which patients it holds.',
                    )
              }
            />
          </div>
        )}
      </Panel>

      <LocationFormDialog
        open={creating !== null}
        onOpenChange={(open) => !open && setCreating(null)}
        parentId={creating?.parentId}
        parentName={creating?.parentName}
      />
    </div>
  );
}
